class GiftWrapPanel extends HTMLElement {
  connectedCallback() {
    this._saveTimer = null;
    this._saving = false;

    // Auto-save 300 ms after the last checkbox toggle (debounced so rapid
    // multi-item selections collapse into a single network round-trip).
    this.querySelectorAll('.gift-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => this.save(), 300);
      });
    });
  }

  _setLoading(active) {
    this._saving = active;
    // Disable all checkboxes while saving to prevent concurrent saves.
    this.querySelectorAll('.gift-checkbox').forEach((cb) => {
      cb.disabled = active;
    });
  }

  async save() {
    if (this._saving) return;
    this._setLoading(true);

    try {
      // ── Resolve gift product variant ID ──────────────────────────────────
      // Priority 1: data attribute set from Liquid linklist (no extra fetch)
      // Priority 2: fetch the product directly by handle
      let giftVariantId = this.dataset.giftVariantId || null;
      if (!giftVariantId) {
        try {
          const p = await fetch('/products/confezione-regalo.js', {
            headers: { Accept: 'application/json' },
          }).then((r) => r.json());
          giftVariantId = p.variants?.[0]?.id ? String(p.variants[0].id) : null;
        } catch (_) {
          console.warn('[GiftWrap] confezione-regalo product not found — pricing skipped');
        }
      }

      // Always fetch a fresh cart — line item keys change when properties change,
      // so any key stored in rendered HTML may be stale.
      const cart = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      const checkboxes = [...this.querySelectorAll('.gift-checkbox')];
      let checkedCount = 0;

      // ── STEP 1: Update _gift_wrap property on each line item ─────────────
      for (const checkbox of checkboxes) {
        const variantId = String(checkbox.dataset.variantId);

        // Match by variant_id (stable across property changes, unlike key).
        // Exclude the gift product itself to avoid self-reference.
        const cartItem = cart.items.find(
          (i) =>
            String(i.variant_id) === variantId &&
            String(i.variant_id) !== String(giftVariantId || '')
        );
        if (!cartItem) continue;

        if (checkbox.checked) checkedCount++;

        // ⚠️  Shopify treats `properties: {}` as "no change". To remove
        // _gift_wrap, pass it explicitly as '' — Shopify then drops the key.
        const properties = { ...(cartItem.properties || {}) };
        properties['_gift_wrap'] = checkbox.checked ? 'true' : '';

        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: cartItem.key, quantity: cartItem.quantity, properties }),
        });
        if (!res.ok) {
          console.error('[GiftWrap] property update failed for variant', variantId, await res.json());
        }
      }

      // ── STEP 2: Sync gift product quantity = number of wrapped items ─────
      if (giftVariantId) {
        const giftItem = cart.items.find((i) => String(i.variant_id) === String(giftVariantId));

        if (checkedCount > 0) {
          if (giftItem) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: giftItem.key, quantity: checkedCount }),
            });
          } else {
            const addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: Number(giftVariantId), quantity: checkedCount }),
            });
            if (!addRes.ok) {
              const err = await addRes.json();
              console.error('[GiftWrap] failed to add gift product:', err.description || err);
            }
          }
        } else if (giftItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
          });
        }
      } else {
        console.warn(
          '[GiftWrap] no gift variant ID — create the gift-wrapping linklist in Shopify admin ' +
            'or verify the confezione-regalo product exists'
        );
      }

      // ── STEP 3: Refresh UI ────────────────────────────────────────────────
      // In the drawer: dispatch cart:refresh (replaces HTML, connectedCallback
      // re-attaches listeners on the new element).
      // On the cart page: reload so the confezione-regalo line item is visible.
      if (document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        this._setLoading(false);
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('[GiftWrap] save failed:', err);
      this._setLoading(false);
    }
  }
}

customElements.define('gift-wrap-panel', GiftWrapPanel);
